import React from "react"
import { FormikErrors } from "formik"
import { DatePicker, DatePickerProps } from "@mui/x-date-pickers/DatePicker"
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3"
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"
import { enGB as en } from "date-fns/locale/en-GB"
import { et } from "date-fns/locale/et"
import { ru } from "date-fns/locale/ru"
import { fi } from "date-fns/locale/fi"
import { lv } from "date-fns/locale/lv"
import { lt } from "date-fns/locale/lt"
import { Typography, TypographyProps, styled } from "@mui/material"

import i18n from "config/i18n"
import { colors } from "theme"

type Props<Generic> = Omit<
  DatePickerProps<Generic>,
  "onChange" | "value" | "id" | "name"
> & {
  light?: boolean
  helperText?: string | FormikErrors<Date> | undefined
  onChange: (value: Date | null) => void
}

const getLocale = () => {
  switch (i18n.language) {
    case "ee":
      return et
    case "fi":
      return fi
    case "lv":
      return lv
    case "lt":
      return lt
    case "ru":
      return ru
    default:
      return en
  }
}

const InputDatePickerComponent = styled(
  ({ light, helperText, ...restProps }: Props<never>) => (
    <LocalizationProvider
      dateAdapter={AdapterDateFns}
      adapterLocale={getLocale()}
    >
      <DatePicker {...restProps} />
      {helperText && typeof helperText === "string" && (
        <HelperText>{helperText}</HelperText>
      )}
    </LocalizationProvider>
  ),
)(({ light = false }) => ({
  "& .MuiInputBase-root": {
    background: light ? colors.white : colors.ghostWhite,
    borderRadius: "9px",
    "& .MuiOutlinedInput-notchedOutline": {
      border: "none",
    },
  },
}))

export const HelperText = styled((props: TypographyProps) => (
  <Typography variant="h6" component="span" {...props}>
    {props.children}
  </Typography>
))(() => ({
  color: colors.ruby,
  fontSize: "0.6875rem",
  fontWeight: 400,
  letterSpacing: "0.6px",
  lineHeight: 1.66,
  margin: "3px 14px 0 14px",
  textAlign: "left",
}))

InputDatePickerComponent.defaultProps = {
  light: false,
}

export default InputDatePickerComponent
